//* Importa bibliotecas do React
import React, { useEffect, useState } from "react";

//* Importa componentes do react-bootstrap
import { Button, Form, Spinner, Alert } from "react-bootstrap";

//* Importa componentes personalizados
import { Attention } from "../../../component/base/Attention";

//* Importa hooks do react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Importa ícones do material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";
import ArchiveRounded from "@material-ui/icons/ArchiveRounded";

//* Importa o hook customizado
import { useCorrections } from "../../../hooks/corrections/useCorrections";
import { useDiagrams } from "../../../hooks/diagrams/useDiagrams";

export function CorrectionForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { applicationId, diagramId, id } = params;


  const [formData, setFormData] = useState<{
    description: string;
    file?: File | null;
  }>({
    description: "",
    file: null,
  });
  // Armazena os dados originais da correção (usado em edição)
  const [originalData, setOriginalData] = useState<any>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<string>("");
  // Estado para armazenar o tipo de questão baseado no diagrama
  const [questionType, setQuestionType] = useState<string>("");

  // Estado para controlar se o usuário deseja alterar o arquivo em edição
  const [isEditingFile, setIsEditingFile] = useState<boolean>(false);

  const {
    getCorrectionById,
    createCorrection,
    updateCorrection,
    isCreating,
    isUpdating,
    error,
  } = useCorrections(token);

  const { getDiagramById } = useDiagrams(token);

  // Validação simples para a descrição (mínimo 3 caracteres)
  const validateDescription = (desc: string): boolean => {
    if (desc.trim().length < 3) {
      setDescriptionError("A descrição deve ter pelo menos 3 caracteres.");
      return false;
    } else {
      setDescriptionError("");
      return true;
    }
  };

  // Busca os dados da correção em modo de edição
  useEffect(() => {
    const fetchCorrectionData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const correctionData = await getCorrectionById(params.id);
          if (correctionData) {
            setFormData({
              description: correctionData.description || "",
              file: null, // Inicialmente, não preenche o file
            });
            setOriginalData(correctionData);
          }
        } catch (err) {
          console.error("Erro ao buscar dados da correção:", err);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchCorrectionData();
  }, [editMode, params.id, getCorrectionById]);


  useEffect(() => {
    const fetchDiagramData = async () => {
      if (diagramId) {
        try {
          const diagramData = await getDiagramById(diagramId);
          if (diagramData && diagramData.type) {
            setQuestionType(diagramData.type);
          }
        } catch (err) {
          console.error("Erro ao buscar dados do diagrama:", err);
        }
      }
    };

    fetchDiagramData();
  }, [diagramId, getDiagramById]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Valida a descrição
    const isValid = validateDescription(formData.description);
    if (!isValid) {
      return;
    }


    try {
      if (editMode && params.id) {
        // Em modo edição, atualiza os dados básicos
        await updateCorrection({
          id: params.id,
          data: { 
            description: formData.description,
            questionType: questionType
          },
        });
        // Se o usuário selecionou um novo arquivo, envie-o
        if (isEditingFile && formData.file) {
          // Cria um FormData para enviar o arquivo
          const updateData = new FormData();
          updateData.append("file", formData.file);

          
          await updateCorrection({
            id: params.id,
            data: { 
              description: originalData.description, 
              file: formData.file,
              questionType: questionType
            },
          });
        }
        navigate(-1);
      } else {
        await createCorrection({
          description: formData.description,
          file: formData.file,
          questionType: questionType
        });
        navigate(-1);
      }
    } catch (err) {
      console.error("Operação falhou:", err);
    }
  };

  const isLoading = isCreating || isUpdating || isLoadingData;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Correção
            </h4>
            <p>
              {editMode
                ? "Edite uma correção do seu sistema"
                : "Cadastre uma correção no seu sistema"}
            </p>
            <br />

            {!isLoading && (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Descrição <Attention />
                  </Form.Label>
                  <Form.Control
                    placeholder="Descrição"
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      validateDescription(e.target.value);
                    }}
                    isInvalid={!!descriptionError}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {descriptionError}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Tipo de Questão <Attention />
                  </Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Objetiva"
                      name="questionType"
                      id="objective"
                      value="objective"
                      checked={questionType === "objective"}
                      onChange={() => setQuestionType("objective")}
                      disabled={true}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Dissertativa"
                      name="questionType"
                      id="dissertative"
                      value="dissertative"
                      checked={questionType === "dissertative"}
                      onChange={() => setQuestionType("dissertative")}
                      disabled={true}
                    />
                  </div>
                </Form.Group>

                {editMode ? (
                  // Em modo edição, exibe o bloco com os dados do arquivo e permite alterar
                  <Form.Group className="mb-3">
                    <Form.Label>Arquivo</Form.Label>
                    {originalData?.fileLabel && !isEditingFile ? (
                      <div className="d-flex align-items-center p-3 border rounded">
                        <ArchiveRounded
                          className="me-3"
                          style={{ fontSize: "2rem" }}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-bold">
                            {originalData.fileLabel}
                          </div>
                          <Button
                            variant="link"
                            onClick={() =>
                              window.open(originalData.fileUrl, "_blank")
                            }
                          >
                            Visualizar / Baixar
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setIsEditingFile(true)}
                          >
                            Alterar arquivo
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Se o usuário optou por alterar o arquivo, exibe o input
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Selecione o novo arquivo (somente .zip ou .rar)
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept=".zip,.rar"
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            const file =
                              target && target.files && target.files.length > 0
                                ? target.files[0]
                                : null;
                            if (file) {
                              const fileExtension = file.name
                                .split(".")
                                .pop()
                                ?.toLowerCase();
                              if (
                                fileExtension !== "zip" &&
                                fileExtension !== "rar"
                              ) {
                                alert(
                                  "Por favor, selecione um arquivo .zip ou .rar"
                                );
                                target.value = "";
                                setFormData({ ...formData, file: null });
                              } else {
                                setFormData({ ...formData, file });
                              }
                            } else {
                              setFormData({ ...formData, file: null });
                            }
                          }}
                        />
                        {isEditingFile && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mt-2"
                            onClick={() => setIsEditingFile(false)}
                          >
                            Cancelar alteração
                          </Button>
                        )}
                      </Form.Group>
                    )}
                  </Form.Group>
                ) : (
                  // Em modo criação, exibe o input de arquivo para seleção
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Arquivo (somente .zip ou .rar) <Attention />
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept=".zip,.rar"
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        const file =
                          target && target.files && target.files.length > 0
                            ? target.files[0]
                            : null;
                        if (file) {
                          const fileExtension = file.name
                            .split(".")
                            .pop()
                            ?.toLowerCase();
                          if (
                            fileExtension !== "zip" &&
                            fileExtension !== "rar"
                          ) {
                            alert(
                              "Por favor, selecione um arquivo .zip ou .rar"
                            );
                            target.value = "";
                            setFormData({ ...formData, file: null });
                          } else {
                            setFormData({ ...formData, file });
                          }
                        } else {
                          setFormData({ ...formData, file: null });
                        }
                      }}
                    />
                  </Form.Group>
                )}

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate(-1)}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Spinner animation="border" variant="primary" role="status" />
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
